import {
    DayOfWeek,
    TimeAccessType,
    TimeAccessTypeLabel,
} from '@treasury/domain/backoffice/services/access/entities/time-access.entity';
import { UserTimeAccessDay } from '@treasury/domain/backoffice/services/access/entities/user-time-access-day.entity';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/jh-ui/tm-button';
import '@treasury/presentation/components/jh-ui/tm-checkbox';
import '@treasury/presentation/components/jh-ui/tm-input';
import '@treasury/presentation/components/jha-wc/tm-select';
import '@treasury/presentation/components/tm-time-picker';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export const timeAccessOptions: { label: TimeAccessTypeLabel; value: TimeAccessType }[] = [
    { label: 'Unlimited', value: TimeAccessType.AllowAllDay },
    { label: 'No Access', value: TimeAccessType.RestrictAllDay },
    { label: 'Time Range', value: TimeAccessType.TimeRange },
];

type TimeAccessTypeChangeEvent = CustomEvent<{
    isValid: boolean;
    value: { label: TimeAccessTypeLabel; value: TimeAccessType };
}>;

@customElement('tm-bo-time-access-day')
export class TimeAccessDayComponent extends TmBaseComponent {
    @property({ type: Object })
    public day?: UserTimeAccessDay;

    @property({ type: Boolean })
    public restricted: boolean = false;

    public render() {
        if (!this.day) {
            return nothing;
        }

        return html` <div class="mb-2 border-b border-gray-200 pb-2 flex items-center">
            <div class="flex-0 w-28">${this.day.dayOfWeekName}</div>

            <div class="flex-0 mr-4">
                <tm-wc-select
                    class="w-[154px]"
                    .options=${timeAccessOptions}
                    .disabled=${!this.restricted}
                    .name=${`timeAccess.days[${this.day.dayOfWeek}].timeAccessType`}
                    .initial=${timeAccessOptions.find(
                        option => option.value === this.day?.timeAccessType
                    )}
                    @value-changed=${this.handleTimeAccessTypeChange}
                >
                </tm-wc-select>
            </div>
            ${this.day.timeAccessType === TimeAccessType.TimeRange
                ? html` <div class="flex-0 flex flex-row items-center mr-4 gap-2">
                      <tm-time-picker
                          .disabled=${!this.restricted}
                          @value-changed=${this.handleBeginTimeChange}
                          .name=${`timeAccess.days[${this.day.dayOfWeek}].beginTime`}
                          .initial=${this.day.beginTime}
                      ></tm-time-picker>
                      <jha-icon-arrow-forward></jha-icon-arrow-forward>
                      <tm-time-picker
                          .disabled=${!this.restricted}
                          @value-changed=${this.handleEndTimeChange}
                          .name=${`timeAccess.days[${this.day.dayOfWeek}].endTime`}
                          .minTime=${this.day.beginTime}
                          .initial=${this.day.endTime}
                      ></tm-time-picker>
                  </div>`
                : nothing}
            ${this.day.dayOfWeek === DayOfWeek.Monday
                ? html`<div class="ml-auto flex-0">
                      <tm-jh-button
                          .disabled=${!this.restricted}
                          .appearance=${'tertiary'}
                          .label=${'Apply to all week days'}
                          @click=${this.handleApplyToAllWeekDays}
                      ></tm-jh-button>
                  </div>`
                : nothing}
        </div>`;
    }

    private handleBeginTimeChange(e: CustomEvent<{ isValid: boolean; value: string }>) {
        if (!this.day) {
            return;
        }

        this.day.beginTime = e.detail.value;
        if (this.day.endTime < this.day.beginTime) {
            this.day.endTime = '';
        }
        this.requestUpdate();
    }

    private handleEndTimeChange(e: CustomEvent<{ isValid: boolean; value: string }>) {
        if (!this.day) {
            return;
        }

        if (e.detail.value < this.day.beginTime) {
            this.day.endTime = '';
        } else {
            this.day.endTime = e.detail.value;
        }
        this.requestUpdate();
    }

    private handleTimeAccessTypeChange(e: TimeAccessTypeChangeEvent) {
        if (!this.day) {
            return;
        }

        this.day.timeAccessType = e.detail.value.value;

        if (this.day.beginTime === '') {
            this.day.beginTime = '8:00 AM';
        }
        if (this.day.endTime === '') {
            this.day.endTime = '5:00 PM';
        }
        this.requestUpdate();
    }

    private handleApplyToAllWeekDays() {
        this.dispatchEvent(
            new CustomEvent('apply-to-all-week-days', {
                detail: {
                    day: this.day,
                },
            })
        );
    }
}
